import axios from 'axios';
import { API_URL, getAuthHeader } from '.';
import { ResponseModel } from '../Models/ResponseModel';
import { Warranty } from '../Models/Warranty';
import { WarrantyPartsReplaced } from '../Models/WarrantyPartsReplaced';
import { WarrantyLabor } from '../Models/WarrantyLabor';
import { WarrantyPhoto } from '../Models/WarrantyPhoto';
import { WarrantyChangeLogEntry } from '../Models/WarrantyChangeLog';

const optionalAuthHeader = () => {
  const token = localStorage.getItem('PriceBrosToken');
  return token ? getAuthHeader() : {};
};

class WarrantyApi {
  static getWarrantyReport(warrantyId: number) {
    return axios.get(`${API_URL}/api/warranty/${warrantyId}/report`, {
      ...getAuthHeader(),
      responseType: 'blob', // Important: treat response as binary blob
    });
  }
  static getWarranty(id: string) {
    return axios.get<Warranty>(
      `${API_URL}/api/warranty/${id}`,
      optionalAuthHeader()
    );
  }
  static getWarranties(userId: number) {
    return axios.get<Warranty[]>(
      `${API_URL}/api/warranty/list/${userId}`,
      getAuthHeader()
    );
  }
  /** Current user's claims (JWT). Use instead of getWarranties for non-admin users. */
  static getMyWarranties() {
    return axios.get<Warranty[]>(`${API_URL}/api/warranty/my`, getAuthHeader());
  }
  static getWarrantyChangeLog(warrantyId: number) {
    return axios.get<WarrantyChangeLogEntry[]>(
      `${API_URL}/api/warranty/${warrantyId}/changelog`,
      getAuthHeader()
    );
  }
  static getWarrantyPartsReplaceds(warrantyId: string) {
    return axios.get<WarrantyPartsReplaced[]>(
      `${API_URL}/api/warranty/parts/${warrantyId}`,
      optionalAuthHeader()
    );
  }
  static getWarrantyLabors(warrantyId: string) {
    return axios.get<WarrantyLabor[]>(
      `${API_URL}/api/warranty/labors/${warrantyId}`,
      optionalAuthHeader()
    );
  }
  static getWarrantyPhotos(warrantyId: string) {
    return axios.get<WarrantyPhoto[]>(
      `${API_URL}/api/warranty/photos/${warrantyId}`,
      optionalAuthHeader()
    );
  }
  static addUpdateWarranty(request: Warranty) {
    return axios.post<ResponseModel>(`${API_URL}/api/warranty`, request, optionalAuthHeader());
  }
  static deleteWarranty(id: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/warranty/${id}`,
      getAuthHeader()
    );
  }
  static deleteWarrantyPart(partId: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/warranty/part/${partId}`,
      optionalAuthHeader()
    );
  }
  static deleteWarrantyLabor(laborId: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/warranty/labor/${laborId}`,
      optionalAuthHeader()
    );
  }
  static deleteWarrantyPhoto(photoId: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/warranty/photo/${photoId}`,
      optionalAuthHeader()
    );
  }
  static addUpdateWarrantyPart(request: WarrantyPartsReplaced) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/warranty/warrantypart`,
      request,
      optionalAuthHeader()
    );
  }
  static addUpdateWarrantyLabor(request: WarrantyLabor) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/warranty/warrantylabor`,
      request,
      optionalAuthHeader()
    );
  }
  static addUpdateWarrantyPhoto(request: WarrantyPhoto) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/warranty/warrantyphoto`,
      request,
      optionalAuthHeader()
    );
  }
  static uploadWarrantyImage(request: FormData) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/warranty/imageupload`,
      request,
      optionalAuthHeader()
    );
  }
  static submitWarrantyRequest(warrantyId: string) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/warranty/submitrequest/${warrantyId}`,
      null
    );
  }
}

export default WarrantyApi;
